// Finar rev-B 24 — veri seti editörü kaydet çubuğu: taslak kirliyken ağacın ALTINA yapışık
// (sticky) belirgin çubuk — "Kaydedilmemiş değişiklikler · [N değişiklik] · Vazgeç · Değişiklikleri
// kaydet". "N değişiklik" düğmesi bekleyen değişikliklerin insan dilindeki listesini açar
// (lib/kpi-set-changes.ts `describeSetChanges`); Escape / dışarı tıklama kapatır. Formül döngüsünde
// kaydet pasif + sebep satırı (P3-27). Kaydetme API'si değişmedi (KpiSetsPage `save`).

import { useEffect, useRef, useState } from "react";
import type { SetChange } from "../../lib/kpi-set-changes";
import { KPI_COPY } from "../../lib/kpi-ui";
import { btnPrimary, btnSmall } from "./kpi-form-styles";

export function SetChangesBar(props: {
  changes: SetChange[];
  /** patch sayısı — özet boş kalırsa (yalnız yeniden numaralama) sayaç yedeği */
  dirtyCount: number;
  saving: boolean;
  cycle: string[] | null;
  onSave: () => void;
  onDiscard: () => void;
}): JSX.Element {
  const { changes, dirtyCount, saving, cycle, onSave, onDiscard } = props;
  const C = KPI_COPY.setChanges;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const count = changes.length > 0 ? changes.length : dirtyCount;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      role="region"
      aria-label={C.bar}
      data-testid="kpi-set-save-bar"
      className="flex items-center gap-2 flex-wrap rounded-xl mt-3"
      style={{
        position: "sticky",
        bottom: 8,
        zIndex: 5,
        padding: "10px 12px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-primary)",
        boxShadow: "var(--shadow-md, 0 8px 24px rgba(15,42,82,.16))",
      }}
    >
      <span className="text-sm font-semibold" style={{ color: "var(--color-text)" }}>
        {C.bar}
      </span>
      {changes.length === 0 ? (
        // rev-B N1 — yalnız yeniden numaralama (görünen sıra aynı): açılacak liste yok
        <span className="text-xs" style={{ color: "var(--color-text-2)" }}>
          {C.count(count)}
        </span>
      ) : (
        <span style={{ position: "relative" }}>
          <button
            ref={toggleRef}
            type="button"
            aria-expanded={open}
            aria-controls="kpi-set-changes-list"
            data-testid="kpi-set-changes-toggle"
            onClick={() => setOpen((v) => !v)}
            style={{
              ...btnSmall,
              color: "var(--color-primary)",
              borderColor: "var(--color-primary)",
              textDecoration: "underline",
            }}
          >
            {C.count(count)}
          </button>
          {open ? (
            <div
              id="kpi-set-changes-list"
              role="region"
              aria-label={C.listTitle}
              className="rounded-lg text-xs"
              style={{
                position: "absolute",
                bottom: "calc(100% + 6px)",
                left: 0,
                width: "min(440px, 80vw)",
                maxHeight: 280,
                overflowY: "auto",
                padding: "10px 12px",
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-md, 0 12px 32px rgba(15,42,82,.18))",
                overflowWrap: "anywhere",
              }}
            >
              <div className="font-semibold mb-1" style={{ color: "var(--color-text)" }}>
                {C.listTitle}
              </div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 16,
                  lineHeight: 1.7,
                  color: "var(--color-text-2)",
                }}
              >
                {changes.map((c, i) => (
                  <li key={`${c.id}:${i.toString()}`}>{c.text}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </span>
      )}
      {cycle ? (
        <span
          className="text-xs"
          style={{ color: "var(--color-error)", flexBasis: "100%", order: 10 }}
          data-testid="kpi-set-cycle-reason"
        >
          {C.cycle(cycle.join(" → "))}
        </span>
      ) : null}
      <span style={{ flex: 1 }} />
      <button type="button" style={btnSmall} onClick={onDiscard} disabled={saving}>
        {C.discard}
      </button>
      <button
        type="button"
        style={{
          ...btnPrimary,
          ...(cycle ? { opacity: 0.5, cursor: "not-allowed" } : {}),
        }}
        onClick={onSave}
        disabled={saving || cycle !== null}
        title={cycle ? C.cycle(cycle.join(" → ")) : undefined}
        aria-busy={saving || undefined}
      >
        {saving ? C.saving : C.save}
      </button>
    </div>
  );
}
